import {
  EventType,
  InteractionRequiredAuthError,
  InteractionType,
  RedirectRequest,
  SilentRequest,
  BrowserAuthError,
} from "@azure/msal-browser";
import { msalInstance } from "./msalInstance";
import { showToast } from "../utils/showToast";
import { fetchMemberOf } from "./graphService";

export const ACCESS_TOKEN_KEY = "accessToken";
export const MS_GRAPH_TOKEN_KEY = "MSGraphToken";

let eventCallbackId: string | null = null;

export default async function refreshTokens(
  accounts = msalInstance.getAllAccounts()
): Promise<void | undefined> {
  if (accounts.length === 0) {
    showToast({
      message:
        "User is no longer signed in. Please refresh the page to sign in again.",
      type: "error",
      id: "userNotSignedIn",
    });
    return;
  }

  const baseRequest:
    | Omit<SilentRequest, "scopes">
    | Omit<RedirectRequest, "scopes"> = {
    account: accounts[0],
  };

  // Only add event callback once to prevent memory leaks
  if (eventCallbackId === null) {
    eventCallbackId = msalInstance.addEventCallback(async (event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.interactionType === InteractionType.Redirect &&
        event.payload !== null &&
        ACCESS_TOKEN_KEY in event.payload &&
        event.payload.accessToken !== null
      ) {
        const azureResponse = event.payload;
        if (!azureResponse)
          throw new Error("No response in acquireAccessToken");

        try {
          const msGraphResponse = await msalInstance.acquireTokenSilent({
            ...baseRequest,
            scopes: process.env.REACT_APP_MS_GRAPH_SCOPE?.split(/\s+/) || [],
          });

          localStorage.setItem(ACCESS_TOKEN_KEY, azureResponse.accessToken);
          localStorage.setItem(MS_GRAPH_TOKEN_KEY, msGraphResponse.accessToken);

          const groups = await fetchMemberOf(msGraphResponse.accessToken, true);
          const groupNames = groups.map((g) => g.toLowerCase());
          localStorage.setItem("userGroups", JSON.stringify(groupNames));
        } catch (error) {
          console.error("Error fetching MS Graph token after redirect:", error);
        }
      }
    });
  }

  try {
    // Try silent token acquisition first
    const azureResponse = await msalInstance.acquireTokenSilent({
      ...baseRequest,
      scopes: process.env.REACT_APP_AZURE_SCOPES?.split(/\s+/) || [],
    });
    const msGraphResponse = await msalInstance.acquireTokenSilent({
      ...baseRequest,
      scopes: process.env.REACT_APP_MS_GRAPH_SCOPE?.split(/\s+/) || [],
    });

    localStorage.setItem(ACCESS_TOKEN_KEY, azureResponse.accessToken);
    localStorage.setItem(MS_GRAPH_TOKEN_KEY, msGraphResponse.accessToken);

    const groups = await fetchMemberOf(msGraphResponse.accessToken, true);
    const groupNames = groups.map((g) => g.toLowerCase());
    localStorage.setItem("userGroups", JSON.stringify(groupNames));
  } catch (silentAuthError) {
    console.error("Silent auth error:", silentAuthError);

    // Check if it's an iframe timeout error (common in Edge)
    if (
      silentAuthError instanceof BrowserAuthError &&
      (silentAuthError.errorCode === "monitor_window_timeout" ||
        silentAuthError.errorCode === "user_cancelled")
    ) {
      console.warn(
        "Iframe blocked - using redirect flow for Edge compatibility"
      );
      try {
        await msalInstance.acquireTokenRedirect({
          ...baseRequest,
          scopes: process.env.REACT_APP_AZURE_SCOPES?.split(/\s+/) || [],
        });
      } catch (redirectAuthError) {
        console.error(`Redirect auth failed`, redirectAuthError);
      }
    } else if (silentAuthError instanceof InteractionRequiredAuthError) {
      // Regular interaction required - use redirect
      try {
        await msalInstance.acquireTokenRedirect({
          ...baseRequest,
          scopes: process.env.REACT_APP_AZURE_SCOPES?.split(/\s+/) || [],
        });
      } catch (redirectAuthError) {
        console.error(`Redirect auth failed`, redirectAuthError);
      }
    } else {
      console.error(`Unexpected auth error:`, silentAuthError);
    }
  }
  return undefined;
}
